import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Card, CardBody } from 'reactstrap';
import { FiMeh } from 'react-icons/fi';
import { useSelector } from 'react-redux';
import { Controller, useForm } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { toast } from 'react-toastify';
import { Chart } from 'chart.js';

import DatePicker from '../../../../../../components/Inputs/DatePicker';
import DashBoardCardItem from '../../../../Protocol/components/DashBoardCardItem';
import AlternativeHeader from '../../../../../../components/Headers/AlternativeHeader.js';
import FlexSelect from '../../../../../../components/Inputs/FlexSelect';
import PieChart from '../../../../Protocol/components/Charts/PieChart';
import ProtocolService from '../../../../../Protocol/service';

import { useRecordTypesByUser } from '../../../../../RecordType/hooks/useRecordTypesByUser';
import { entitiesName } from '../../../../../CRM/shared/utils/entitiesName';
import { dateRules } from '../../../../../Protocol/form/assets/functions';
import { ErrorFormMessage } from '../../../../../../components/ErrorFormMessage';
import { getDateBegin } from '../../../../../Report/Audit/assets/getDateBegin';
import { toISOFormat } from 'utils/fuctions/formattedToISOFormatofdate';
import { CardsItemWrapper } from '../../../../Protocol/styled';
import { errorFormMessage } from '../../../../../ServicePanel/components/AttendanceContent/components/Modal/CloseConversation/atonsComponents/ErrorFormMessage';
import { calculateData } from '../../../../../Report/Protocol/Synthetic/assets/CalculateAndFormatedData';
import { TotalProtocolsPerStatus } from 'views/Report/Protocol/Synthetic/components/Graphics/components/TotalProtocolsPerStatus/TotalProtocolsPerStatus';

Chart.plugins.register(ChartDataLabels);

const ProtocolTabs = () => {
  const systemConfigurationPrimaryColor = useSelector(
    (state) => state.systemConfiguration.primary_color
  );
  const { recordTypes, isLoadingRecordTypes, getRecordTypesByUser } =
    useRecordTypesByUser(entitiesName.PROTOCOL);

  const { calculateTotalProtocolsPerServiceTypeForPieChart } = calculateData();

  const { control, setValue, errors, getValues, watch } = useForm();
  const [selectedRecordType, setSelectedRecordType] = useState(null);
  const [protocolData, setProtocolData] = useState(null);
  const [protocolSLAData, setProtocolSLAData] = useState(null);
  const [isUsingSLA, setIsUsingSLA] = useState(false);

  const watchDateBegin = watch('dateBegin');
  const watchDateEnd = watch('dateEnd');

  useEffect(() => {
    if (recordTypes && recordTypes.length > 0) {
      const initialRecordType = {
        label: recordTypes[0].name,
        value: recordTypes[0].id,
        is_sla_active: recordTypes[0].is_sla_active
      };
      setSelectedRecordType(initialRecordType);
      setIsUsingSLA(recordTypes[0].is_sla_active);
    }
  }, [recordTypes]);

  useEffect(() => {
    if (!watchDateBegin || !watchDateEnd || !selectedRecordType) return;
    const login = localStorage.getItem('login');
    if (!login) return;
    const paramsToGetNonSLAProtocolInfo = {
      dateBegin: watchDateBegin,
      dateEnd: watchDateEnd,
      recordType: selectedRecordType.value
        ? selectedRecordType.value
        : selectedRecordType,
      'current_responsible[]': login
    };
    const paramsToGetSLAProtocolInfo = {
      dateBegin: `${toISOFormat(watchDateBegin)}`,
      dateEnd: `${toISOFormat(watchDateEnd)}`
    };
    ProtocolService.getReportProtocol(
      { ...paramsToGetNonSLAProtocolInfo },
      'synthetic'
    )
      .then((response) => {
        const data = response?.data.content;
        setProtocolData(data);
      })
      .catch((err) => {
        console.log('err', err);
        toast.error('Erro desconhecido por favor, informar o suporte!', {
          autoClose: 3000,
          closeOnClick: true
        });
      });

    if (isUsingSLA) {
      ProtocolService.getDashboardAgentInfo(
        paramsToGetSLAProtocolInfo,
        selectedRecordType.value ? selectedRecordType.value : selectedRecordType
      )
        .then((response) => {
          const data = response?.data.data;
          setProtocolSLAData(data);
        })
        .catch((err) => {
          console.log('err', err);
          toast.error('Erro desconhecido por favor, informar o suporte!', {
            autoClose: 3000,
            closeOnClick: true
          });
        });
    }
  }, [watchDateBegin, watchDateEnd, selectedRecordType, isUsingSLA]);

  useEffect(() => {
    getRecordTypesByUser();
  }, [getRecordTypesByUser]);

  const handleRecordTypeChange = (selectedOption) => {
    setSelectedRecordType(selectedOption);
    setIsUsingSLA(selectedOption.is_sla_active);
  };

  useEffect(() => {
    if (recordTypes) {
      const selectedType = recordTypes.find(
        (type) => type.id === selectedRecordType
      );
      if (selectedType) {
        setProtocolSLAData(selectedType.slaData);
        setIsUsingSLA(selectedType.is_sla_active);
      }
    }
  }, [recordTypes, selectedRecordType]);

  const getSelectOptionsDTO = () => {
    if (!recordTypes) return [];
    return recordTypes.map((group) => ({
      label: group.name,
      value: group.id,
      is_sla_active: group.is_sla_active
    }));
  };

  const renderContent = () => {
    if (protocolData && protocolSLAData) {
      return (
        <>
          <Row
            className="mb-3"
            style={{
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <CardsItemWrapper>
              <DashBoardCardItem
                title="Total de protocolos novos"
                text={`${
                  protocolSLAData ? protocolSLAData.newProtocolsCount : 0
                }`}
                icon="ni ni-single-copy-04"
              />
              <DashBoardCardItem
                title="Total de protocolos"
                text={`${
                  protocolSLAData ? protocolSLAData.allProtocolsCount : 0
                }`}
                icon="ni ni-single-copy-04"
              />
              <DashBoardCardItem
                title="Total de protocolos abertos"
                text={`${
                  protocolSLAData ? protocolSLAData.openProtocolsCount : 0
                }`}
                icon="ni ni-single-copy-04"
              />
              <DashBoardCardItem
                title="Total de protocolos fechados"
                text={`${
                  protocolSLAData ? protocolSLAData.closedProtocolsCount : 0
                }`}
                icon="ni ni-single-copy-04"
              />
            </CardsItemWrapper>
          </Row>
          <Row className="mb-3">
            <Col md="8">
              {protocolData?.totalProtocolsPerStatus && (
                <TotalProtocolsPerStatus protocolsData={protocolData} />
              )}
            </Col>
            <Col md="4">
              <Card>
                {protocolSLAData && (
                  <PieChart
                    formattedDataProtocols={
                      calculateTotalProtocolsPerServiceTypeForPieChart(
                        protocolSLAData.pieChartDataServiceType
                      ) || []
                    }
                    chartTittle="Protocolos por classificação de serviço"
                  />
                )}
              </Card>
            </Col>
          </Row>
        </>
      );
    } else if (protocolData) {
      return (
        <>
          <Row className="mb-3">
            <Col md="8">
              {protocolData?.totalProtocolsPerStatus && (
                <TotalProtocolsPerStatus protocolsData={protocolData} />
              )}
            </Col>
            <Col md="4">
              <Card>
                {protocolSLAData && (
                  <PieChart
                    formattedDataProtocols={
                      calculateTotalProtocolsPerServiceTypeForPieChart(
                        protocolSLAData.pieChartDataServiceType
                      ) || []
                    }
                    chartTittle="Protocolos por classificação de serviço"
                  />
                )}
              </Card>
            </Col>
          </Row>
        </>
      );
    } else {
      return (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <p
            className="mb-1 text-muted"
            style={{
              padding: '0.5rem',
              fontWeight: 'bold',
              fontSize: '1.2rem'
            }}
          >
            Nenhum dado disponível.
          </p>
        </div>
      );
    }
  };

  return (
    <>
      <AlternativeHeader />

      {!isLoadingRecordTypes && !recordTypes?.length ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <p
            className="mb-1 text-muted"
            style={{
              padding: '0.5rem',
              fontWeight: 'bold',
              fontSize: '1.2rem'
            }}
          >
            Você não tem acesso a nenhum tipo de protocolo.
          </p>
          <FiMeh size={25} color={systemConfigurationPrimaryColor} />
        </div>
      ) : (
        <Container className="mt--6" fluid>
          <Card className="mb-3">
            <CardBody>
              <Row className="mb-3">
                <Col md="3">
                  <label className="form-control-label">
                    Tipo de registro*
                  </label>
                  <Controller
                    render={(props) => (
                      <FlexSelect
                        dataOptions={getSelectOptionsDTO() || []}
                        isMulti={false}
                        value={props.value}
                        valueController={setValue}
                        fieldName="selectedRecordType"
                        labelName="label"
                        valueName="value"
                        onChange={handleRecordTypeChange}
                        closeMenuOnSelect={true}
                        placeholder="Tipo de registro do protocolo"
                      />
                    )}
                    control={control}
                    defaultValue={
                      selectedRecordType ? selectedRecordType : null
                    }
                    rules={{
                      required: 'Campo obrigatório'
                    }}
                    name="selectedRecordType"
                  />
                  <ErrorMessage
                    errors={errors}
                    name="selectedRecordType"
                    render={({ message }) => errorFormMessage(message)}
                  />
                </Col>
                <Col md="4">
                  <label className="form-control-label">Data inicial</label>
                  <Controller
                    render={(props) => (
                      <DatePicker
                        fieldName="dateBegin"
                        valueController={setValue}
                        defaultValue={props.value}
                      />
                    )}
                    control={control}
                    name="dateBegin"
                    rules={dateRules}
                    defaultValue={getDateBegin()}
                  />
                  <ErrorMessage
                    errors={errors}
                    name="dateBegin"
                    render={({ message }) => (
                      <ErrorFormMessage message={message} />
                    )}
                  />
                </Col>
                <Col md="4">
                  <label className="form-control-label">Data final</label>
                  <Controller
                    render={(props) => (
                      <DatePicker
                        fieldName="dateEnd"
                        valueController={setValue}
                        defaultValue={props.value}
                      />
                    )}
                    control={control}
                    name="dateEnd"
                    defaultValue={new Date().toLocaleDateString('pt-br')}
                    rules={{
                      ...dateRules,
                      validate: async (value) => {
                        if (
                          new Date(await toISOFormat(value, true)) <
                          new Date(
                            await toISOFormat(getValues()['dateBegin'], true)
                          )
                        ) {
                          return 'A data final não pode ser anterior à inicial!';
                        } else {
                          return true;
                        }
                      }
                    }}
                  />
                  <ErrorMessage
                    errors={errors}
                    name="dateEnd"
                    render={({ message }) => (
                      <ErrorFormMessage message={message} />
                    )}
                  />
                </Col>
              </Row>
            </CardBody>
          </Card>
          <CardBody>{renderContent()}</CardBody>
        </Container>
      )}
    </>
  );
};

export default ProtocolTabs;
